import { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { API_BASE } from '../lib/config';
import FeedbackButton from '../components/FeedbackButton';
import { checkAndRestoreActivityCache } from '../lib/claudeApi';

function History() {
  const navigate = useNavigate();
  const [destinations, setDestinations] = useState([]);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [loading, setLoading] = useState(true);
  const topRef = useRef(null);

  const TIER_COLORS = {
    iconic: '#c9a84c',
    underrated: '#7c6fcd',
    hidden_gem: '#2d9b6f',
  };

  const TIER_LABELS = {
    iconic: '⭐ Iconic',
    underrated: '🧭 Underrated',
    hidden_gem: '💎 Hidden Gem',
  };

  function fetchHistory(pageNum) {
    const userId = sessionStorage.getItem('carpeUserId');
    if (!userId) {
      navigate('/');
      return;
    }
    setLoading(true);
    fetch(`${API_BASE}/auth/history/destinations/${userId}?page=${pageNum}&page_size=5`)
      .then(res => res.json())
      .then(data => {
        setDestinations(data.destinations || []);
        setTotalPages(data.total_pages || 1);
        setLoading(false);
      })
      .catch(() => {
        setLoading(false);
      });
  }

  useEffect(() => {
    fetchHistory(1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [navigate]);

  function handlePageChange(newPage) {
    setPage(newPage);
    fetchHistory(newPage);
    topRef.current?.scrollIntoView({ behavior: 'smooth' });
  }

  function handleSeizeActivities(dest) {
    const userId = sessionStorage.getItem('carpeUserId');
    const prof = JSON.parse(sessionStorage.getItem('carpeWonderProfile') || '{}');
    sessionStorage.setItem('carpeDestination', JSON.stringify({
      name: dest.destination_name,
      country: dest.country,
      tier: dest.tier,
      match_reason: dest.match_reason,
    }));
    sessionStorage.setItem('carpeActivitiesReferrer', '/history');
    if (userId) {
      checkAndRestoreActivityCache(userId, dest.destination_name, null)
        .then(restored => {
          if (restored) {
            // Cached — skip loading screen
            navigate('/activities');
          } else {
            // Not cached — go through full loading flow
            sessionStorage.setItem('carpeLoadingContext', JSON.stringify({
              type: 'activities',
              endpoint: '/recommendations/activities',
              payload: {
                destination: dest.destination_name,
                country: dest.country || null,
                state_province: null,
                profile: {
                  profile: prof.profile,
                  pace: prof.pace,
                  activity_level: prof.activity_level,
                  best_for_tags: prof.best_for_tags,
                  social_vibe: prof.social_vibe,
                  explorer_score: prof.explorer_score,
                  activity_profile: prof.activity_profile,
                  activity_dimensions: prof.activity_dimensions,
                  activity_profile_description: prof.activity_profile_description,
                },
                micro_answers: JSON.parse(sessionStorage.getItem('carpeMicroAnswers') || '{}'),
                teaser_activities: [],
              },
              destination: '/activities',
              loadingMessage: 'Finding the best experiences in ' + dest.destination_name + '...',
              destinationFacts: [],
            }));
            navigate('/loading');
          }
        })
        .catch(() => navigate('/welcome'));
    } else {
      navigate('/welcome');
    }
  }

  return (
    <div style={styles.page}>
      <FeedbackButton />
      <div style={styles.container} ref={topRef}>
        <div style={styles.logo}>Carpe Wonder™</div>
        <button style={styles.backBtn} onClick={() => navigate('/welcome')}>
          ← Back
        </button>
        <h1 style={styles.headline}>My Destination History</h1>
        <p style={styles.subline}>Every destination we've found for you.</p>

        {loading ? (
          <div style={styles.loading}>Loading your history...</div>
        ) : destinations.length === 0 ? (
          <div style={styles.empty}>
            No destination history yet. Take the quiz to get started.
          </div>
        ) : (
          <>
            {destinations.map((dest, i) => (
              <div key={i} style={styles.card}>
                <div style={styles.cardHeader}>
                  <div>
                    <div style={styles.destName}>{dest.destination_name}</div>
                    <div style={styles.destCountry}>{dest.country}</div>
                  </div>
                  <div style={{
                    ...styles.tierBadge,
                    background: `${TIER_COLORS[dest.tier]}22`,
                    color: TIER_COLORS[dest.tier],
                    border: `0.5px solid ${TIER_COLORS[dest.tier]}55`,
                  }}>
                    {TIER_LABELS[dest.tier]}
                  </div>
                </div>
                {dest.match_reason && (
                  <div style={styles.matchReason}>{dest.match_reason}</div>
                )}
                <button
                  style={styles.seizeBtn}
                  onClick={() => handleSeizeActivities(dest)}
                >
                  Seize activities →
                </button>
              </div>
            ))}

            {totalPages > 1 && (
              <div style={styles.pagination}>
                {Array.from({ length: totalPages }, (_, i) => i + 1).map(p => (
                  <button
                    key={p}
                    style={{
                      ...styles.pageBtn,
                      background: p === page ? 'var(--gold)' : 'transparent',
                      color: p === page ? '#0d0d1f' : 'rgba(255,255,255,0.5)',
                      border: p === page ? 'none' : '1px solid rgba(255,255,255,0.2)',
                    }}
                    onClick={() => handlePageChange(p)}
                  >
                    {p}
                  </button>
                ))}
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}

const styles = {
  page: {
    minHeight: '100vh',
    background: '#0d0d1f',
    display: 'flex',
    justifyContent: 'center',
    padding: '24px 16px',
  },
  container: {
    width: '100%',
    maxWidth: '480px',
  },
  logo: {
    fontSize: '14px',
    color: 'var(--gold)',
    letterSpacing: '0.14em',
    textTransform: 'uppercase',
    marginBottom: '16px',
    textAlign: 'center',
  },
  backBtn: {
    background: 'none',
    border: 'none',
    color: 'rgba(255,255,255,0.5)',
    fontSize: '14px',
    cursor: 'pointer',
    padding: '0',
    marginBottom: '24px',
  },
  headline: {
    fontSize: '24px',
    fontWeight: '700',
    color: '#fff',
    marginBottom: '8px',
  },
  subline: {
    fontSize: '14px',
    color: 'rgba(255,255,255,0.5)',
    marginBottom: '24px',
  },
  loading: {
    color: 'rgba(255,255,255,0.5)',
    textAlign: 'center',
    padding: '40px 0',
  },
  empty: {
    color: 'rgba(255,255,255,0.5)',
    textAlign: 'center',
    padding: '40px 0',
  },
  card: {
    background: '#1a1a35',
    borderRadius: '16px',
    padding: '20px',
    marginBottom: '16px',
    border: '0.5px solid rgba(255,255,255,0.08)',
  },
  cardHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    marginBottom: '12px',
  },
  destName: {
    fontSize: '18px',
    fontWeight: '600',
    color: '#fff',
    marginBottom: '2px',
  },
  destCountry: {
    fontSize: '13px',
    color: 'rgba(255,255,255,0.5)',
  },
  tierBadge: {
    fontSize: '11px',
    padding: '4px 10px',
    borderRadius: '20px',
    fontWeight: '500',
    whiteSpace: 'nowrap',
  },
  matchReason: {
    fontSize: '14px',
    color: 'rgba(255,255,255,0.7)',
    lineHeight: '1.5',
    marginBottom: '16px',
  },
  seizeBtn: {
    width: '100%',
    background: 'transparent',
    color: 'var(--gold)',
    border: '1px solid rgba(201,168,76,0.4)',
    borderRadius: '28px',
    padding: '12px',
    fontSize: '14px',
    fontWeight: '600',
    cursor: 'pointer',
  },
  pagination: {
    display: 'flex',
    justifyContent: 'center',
    gap: '8px',
    marginTop: '24px',
    marginBottom: '40px',
  },
  pageBtn: {
    width: '36px',
    height: '36px',
    borderRadius: '50%',
    fontSize: '14px',
    fontWeight: '600',
    cursor: 'pointer',
  },
};

export default History;